// 모듈 레지스트리 저장소 — 순수 함수(moduleRegistry.ts)에 현재 값 하나와 구독을 얹은 얇은 층.
// 라우팅이 동기라서 처음에는 폴백(또는 localStorage 캐시)으로 시작하고, API 가 오면 교체한다.
// 교체해도 같은 경로는 같은 번들로 풀리므로 keep-alive 패널(번들 코드가 key)은 다시 마운트되지 않는다.
import { useEffect, useState } from 'react';
import { ModuleEntry, FALLBACK_REGISTRY, buildRegistry, sameRegistry, moduleCodeForPath as codeForPath } from './moduleRegistry';

/** 마지막 성공 응답(원시 배열)을 두는 키 — 다음 시작 때 즉시 쓰고 백그라운드로 갱신한다 */
const CACHE_KEY = 'moduleRegistryCache';
/** 모듈 표 응답 대기 상한 — 넘기면 조용히 폴백/캐시로 산다 */
const FETCH_TIMEOUT_MS = 5000;

function readCache(): unknown {
  try {
    const text = localStorage.getItem(CACHE_KEY);
    return text ? JSON.parse(text) : null;
  } catch {
    return null; // 막힌 저장소·깨진 JSON — 폴백으로 산다
  }
}

function writeCache(raw: unknown): void {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(raw));
  } catch {
    // 저장 실패는 무시 — 캐시는 편의일 뿐이다
  }
}

let current: ModuleEntry[] = FALLBACK_REGISTRY;
const listeners = new Set<() => void>();

function set(next: ModuleEntry[]): void {
  if (sameRegistry(current, next)) return;
  current = next;
  listeners.forEach(l => l());
}

// 첫 렌더 전에 캐시를 반영한다(없거나 깨졌으면 폴백 그대로)
set(buildRegistry(readCache()));

export function getRegistry(): ModuleEntry[] {
  return current;
}

/** 훅 밖(ModuleHost.moduleCodeForPath 등)에서 현재 레지스트리로 경로를 푼다 */
export function currentModuleCodeForPath(pathname: string): string | null {
  return codeForPath(current, pathname);
}

/** 현재 레지스트리를 구독 — API 응답이 오면 사용처가 함께 갱신된다 */
export function useModuleRegistry(): ModuleEntry[] {
  const [value, setValue] = useState<ModuleEntry[]>(current);
  useEffect(() => {
    const sync = () => setValue(current);
    listeners.add(sync);
    sync(); // 구독 전에 바뀌었을 수 있다
    return () => { listeners.delete(sync); };
  }, []);
  return value;
}

/**
 * 모듈 표를 한 번 읽어 레지스트리를 갱신한다. 실패·타임아웃·잘못된 응답은 콘솔 경고 한 줄만 남기고
 * 현재 값(폴백/캐시)을 유지한다 — 화면을 막지 않는다.
 * fetcher 는 api/modules.ts 의 fetchModules (순환 import 를 피하려고 인자로 받는다).
 */
export function loadModuleRegistry(fetcher: (signal: AbortSignal) => Promise<unknown>): Promise<void> {
  const ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = ctrl ? setTimeout(() => ctrl.abort(), FETCH_TIMEOUT_MS) : null;
  return fetcher(ctrl ? ctrl.signal : (undefined as unknown as AbortSignal))
    .then(raw => {
      if (!Array.isArray(raw)) return; // 옛 서버 — 폴백/캐시 유지. 빈 배열은 「이 사용자에게 보일 모듈이 없다」는 확정 응답이다(권한으로 거른다)
      writeCache(raw);
      set(buildRegistry(raw));
    })
    .catch(err => {
      console.warn('[moduleRegistry] 모듈 목록을 불러오지 못해 내장 목록을 씁니다:', err && err.message ? err.message : err);
    })
    .then(() => { if (timer) clearTimeout(timer); });
}

/** 로그인 이후 레이아웃이 마운트될 때 한 번 호출하는 훅 */
export function useLoadModuleRegistry(fetcher: (signal: AbortSignal) => Promise<unknown>): void {
  useEffect(() => {
    loadModuleRegistry(fetcher);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
